import React from "react"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"
import { makeCalendarSettingsId } from "../../calendar-settings/api/calendar-settings-id"
import { makeCalendarSettingsKey } from "../../calendar-settings/utils/settings-key"
import { useCalendarSettingsById } from "../../hooks/use-calendar-settings-by-id"

type ProjectCalendarSettingsProviderProps = {
  children: React.ReactNode
  errorFallback?: React.ReactNode
  loadingFallback?: React.ReactNode
  projectId?: string
}

type ProjectCalendarSettingsContextValue = {
  calendarSettings: CalendarSettingsViewModel
  settingsKey: string
}

const queryOptions = { refetchOnMountOrArgChange: true } as const

const ProjectCalendarSettingsContext = React.createContext<ProjectCalendarSettingsContextValue | undefined>(undefined)

export const ProjectCalendarSettingsProvider = ({
  children,
  errorFallback = null,
  loadingFallback = null,
  projectId,
}: ProjectCalendarSettingsProviderProps) => {
  const calendarSettingsId = projectId ? makeCalendarSettingsId({ type: "project", id: projectId }) : undefined
  const { calendarSettings, isLoading, isError } = useCalendarSettingsById(calendarSettingsId, queryOptions)

  const settingsKey = React.useMemo(() => {
    if (!projectId) return ""
    return makeCalendarSettingsKey(calendarSettings)
  }, [calendarSettings, projectId])

  if (isLoading) return <>{loadingFallback}</>
  if (isError || !calendarSettings) return <>{errorFallback}</>

  return (
    <ProjectCalendarSettingsContext.Provider
      value={{
        calendarSettings,
        settingsKey,
      }}
    >
      {children}
    </ProjectCalendarSettingsContext.Provider>
  )
}

export const useProjectCalendarSettings = () => {
  const context = React.useContext(ProjectCalendarSettingsContext)
  if (!context) {
    throw new Error("useProjectCalendarSettings must be used within a ProjectCalendarSettingsProvider")
  }
  return context
}
